import { CreateRequestLayout } from "@/components/customer/CreateRequestLayout";
import { LiveTrackingMap } from "@/components/map/live-tracking-map";
import GooglePlacesInput from "@/components/place/google-places-input";
import { ThemedText } from "@/components/themed-text";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import {
  useDesktopLayout,
  useDesktopSheetStyle,
} from "@/hooks/use-desktop-layout";
import {
  formatCoordinateAddress,
  getFreshPassengerLocation,
  resolvePassengerLocationCoords,
} from "@/lib/utils/passenger-location";
import {
  BookingStep2Data,
  getBookingStep2Schema,
  isLocationOnlyService,
} from "@/lib/validation/booking";
import { useCreateRequestStore } from "@/stores/use-create-request-store";
import { useLocationStore } from "@/stores/use-location-store";
import BottomSheet, {
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetView,
} from "@gorhom/bottom-sheet";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useRouter } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { GooglePlacesAutocompleteRef } from "react-native-google-places-autocomplete";
import MapView from "react-native-maps";
import { ActivityIndicator, HelperText } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

type AddressRegistrationProps = {
  route?: boolean;
  submitLabel?: string;
  onComplete?: (data: BookingStep2Data) => void | Promise<void>;
  onBack?: () => void;
  serviceType?: string;
  desktopLayout?: boolean;
};

export const AddressRegistration = ({
  route = true,
  submitLabel = "Next",
  onComplete,
  onBack,
  serviceType: serviceTypeOverride,
  desktopLayout = false,
}: AddressRegistrationProps) => {
  const router = useRouter();
  const { theme } = useAuth();
  const { formData, setFormData, setCurrentStep } = useCreateRequestStore();
  const mapRef = useRef<MapView>(null);
  const markerRef = useRef<MapView>(null);
  const isMountedRef = useRef(true);
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { coordinate, setCoordinate } = useLocationStore((state) => state);
  const pickupRef = useRef<GooglePlacesAutocompleteRef>(null);
  const dropoffRef = useRef<GooglePlacesAutocompleteRef>(null);
  const bottomSheetRef = useRef<BottomSheet>(null);
  const routeSheetRef = useRef<BottomSheetModal>(null);
  const snapPoints = useMemo(() => ["25%"], []);
  const routeSheetSnapPoints = useMemo(() => ["55%", "95%"], []);
  const desktopSheetStyle = useDesktopSheetStyle();
  const desktop = useDesktopLayout();

  const serviceType = serviceTypeOverride ?? formData.service_type;
  const locationOnly = isLocationOnlyService(serviceType);
  const step2Schema = getBookingStep2Schema(serviceType);

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<BookingStep2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      from_address: formData.from_address || "",
      from_latitude: formData.from_latitude || 0,
      from_longitude: formData.from_longitude || 0,
      to_address: formData.to_address || "",
      to_latitude: formData.to_latitude || 0,
      to_longitude: formData.to_longitude || 0,
    },
  });

  const pickupAddress = watch("from_address");
  const pickupLat = watch("from_latitude");
  const pickupLng = watch("from_longitude");
  const dropoffAddress = watch("to_address");
  const dropoffLat = watch("to_latitude");
  const dropoffLng = watch("to_longitude");

  const applyPickupCoordinates = useCallback(
    (latitude: number, longitude: number) => {
      setCoordinate({ latitude, longitude, heading: null, speed: 0 });
      setValue("from_latitude", latitude);
      setValue("from_longitude", longitude);
    },
    [setCoordinate, setValue],
  );

  const applyPassengerLocation = useCallback(
    (latitude: number, longitude: number, address: string | null) => {
      if (!isMountedRef.current) {
        return;
      }

      const resolvedAddress =
        address ?? formatCoordinateAddress(latitude, longitude);
      applyPickupCoordinates(latitude, longitude);
      setValue("from_address", resolvedAddress);
      pickupRef.current?.setAddressText(resolvedAddress);
    },
    [applyPickupCoordinates, setValue],
  );

  const resolvePassengerLocation = useCallback(
    async ({
      forceRefresh = false,
      showSpinner = true,
    }: {
      forceRefresh?: boolean;
      showSpinner?: boolean;
    } = {}) => {
      if (!isMountedRef.current) {
        return;
      }

      if (showSpinner) {
        setIsResolvingLocation(true);
        setValue("from_address", "Getting your location...");
      }

      try {
        const result = await resolvePassengerLocationCoords({
          forceRefresh,
          onSeed: (latitude, longitude, address) => {
            applyPassengerLocation(latitude, longitude, address);
            // Clear spinner once we have a seed, unless this is an explicit
            // locate refresh (keep spinner until the fresh fix completes).
            if (isMountedRef.current && showSpinner && !forceRefresh) {
              setIsResolvingLocation(false);
            }
          },
        });

        if (!isMountedRef.current) {
          return;
        }

        if (!result || result.permissionDenied) {
          if (showSpinner) {
            setValue("from_address", "Enter your address");
          }
          return;
        }

        applyPassengerLocation(
          result.latitude,
          result.longitude,
          result.address,
        );
      } catch (error) {
        console.error("Failed to fetch passenger location:", error);
        if (isMountedRef.current && showSpinner) {
          setValue("from_address", "Enter your address");
        }
      } finally {
        if (isMountedRef.current) {
          setIsResolvingLocation(false);
        }
      }
    },
    [applyPassengerLocation, setValue],
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (formData.to_address) {
      dropoffRef.current?.setAddressText(formData.to_address);
    }

    const hasSavedPickup =
      !!formData.from_address &&
      !!formData.from_latitude &&
      !!formData.from_longitude;

    if (hasSavedPickup) {
      pickupRef.current?.setAddressText(formData.from_address ?? "");
      return () => {
        isMountedRef.current = false;
      };
    }

    const fresh = getFreshPassengerLocation();
    if (fresh) {
      applyPassengerLocation(fresh.latitude, fresh.longitude, fresh.address);
      void resolvePassengerLocation({
        forceRefresh: true,
        showSpinner: false,
      });
    } else if (coordinate?.latitude && coordinate?.longitude) {
      applyPassengerLocation(
        coordinate.latitude,
        coordinate.longitude,
        useLocationStore.getState().resolvedAddress,
      );
      void resolvePassengerLocation({
        forceRefresh: true,
        showSpinner: false,
      });
    } else {
      void resolvePassengerLocation({ showSpinner: true });
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openRouteSheet = useCallback(
    (focusTarget?: "pickup" | "dropoff") => {
      routeSheetRef.current?.present();

      if (focusTarget === "pickup") {
        setTimeout(() => {
          pickupRef.current?.setAddressText(pickupAddress ?? "");
          dropoffRef.current?.setAddressText(dropoffAddress ?? "");
          pickupRef.current?.focus();
        }, 200);
      } else if (focusTarget === "dropoff") {
        setTimeout(() => {
          pickupRef.current?.setAddressText(pickupAddress ?? "");
          dropoffRef.current?.setAddressText(dropoffAddress ?? "");
          dropoffRef.current?.focus();
        }, 200);
      }
    },
    [pickupAddress, dropoffAddress],
  );

  const closeRouteSheet = () => {
    routeSheetRef.current?.dismiss();
  };

  const onSubmit = async (data: BookingStep2Data) => {
    if (onComplete) {
      try {
        setIsSubmitting(true);
        await onComplete(data);
      } finally {
        if (isMountedRef.current) {
          setIsSubmitting(false);
        }
      }
      return;
    }

    setFormData(data);
    setCurrentStep(3);
    if (route) {
      router.push("/customer/create-request/step3");
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (route) {
      router.back();
      return;
    }

    setCurrentStep(1);
  };

  const addressTitle = locationOnly
    ? "Confirm your vehicle location to continue."
    : serviceType === "mobile_mechanics"
      ? "Plan a vehicle repair in seconds."
      : serviceType === "mobile_tyre_fitting"
        ? "Plan a tyre replacement in seconds."
        : "Plan a vehicle transportation in seconds.";

  const mapContent = (
    <>
      <LiveTrackingMap
        mapRef={mapRef}
        markerRef={markerRef}
        showTruck={false}
        pickupLocation={
          pickupLat && pickupLng
            ? {
                latitude: Number(pickupLat),
                longitude: Number(pickupLng),
              }
            : null
        }
        dropoffLocation={
          dropoffLat && dropoffLng
            ? {
                latitude: Number(dropoffLat),
                longitude: Number(dropoffLng),
              }
            : null
        }
        style={styles.map}
      />

      <TouchableOpacity
        testID="pickup-point-badge"
        accessible
        accessibilityRole="button"
        accessibilityLabel={`Pick up point, ${pickupAddress ?? "Enter your address"}`}
        style={[styles.pickupBadge, { backgroundColor: theme.background }]}
        onPress={() => openRouteSheet("pickup")}
        activeOpacity={0.9}
      >
        <View style={styles.locationIconContainer}>
          <Ionicons name="location" size={18} color={BrandColors.white} />
        </View>
        <View style={styles.badgeTextContainer}>
          <ThemedText style={styles.badgeLabel}>
            {isLocationOnlyService(serviceType)
              ? "Vehicle Location"
              : "Pick up point"}
          </ThemedText>
          <ThemedText style={styles.badgeValue} numberOfLines={1}>
            {isResolvingLocation && !pickupAddress
              ? "Getting your location..."
              : pickupAddress}
          </ThemedText>
        </View>
        {isResolvingLocation ? (
          <ActivityIndicator size="small" color={BrandColors.primary} />
        ) : (
          <Ionicons
            name="chevron-forward"
            size={20}
            color={BrandColors.mediumGray}
          />
        )}
      </TouchableOpacity>

      <TouchableOpacity
        style={[
          styles.locateButton,
          desktopLayout && styles.desktopLocateButton,
        ]}
        onPress={() =>
          void resolvePassengerLocation({
            forceRefresh: true,
            showSpinner: true,
          })
        }
        disabled={isResolvingLocation}
        accessibilityRole="button"
        accessibilityLabel="Use my current location"
      >
        {isResolvingLocation ? (
          <ActivityIndicator size="small" color="#0f172a" />
        ) : (
          <Ionicons name="navigate" size={20} color="#0f172a" />
        )}
      </TouchableOpacity>
    </>
  );

  const deliveryAddressField = locationOnly ? null : (
    <>
      <TouchableOpacity
        testID="delivery-address-input"
        accessible
        accessibilityRole="button"
        accessibilityLabel={
          dropoffAddress ? dropoffAddress : "Delivery address"
        }
        style={[
          styles.searchCard,
          {
            backgroundColor: theme.background,
            borderColor: theme.border,
          },
          errors.to_address && styles.inputError,
        ]}
        onPress={() => openRouteSheet("dropoff")}
      >
        <Ionicons
          name="search"
          size={20}
          color={theme.text}
          accessible={false}
        />
        <View style={{ flex: 1 }} accessible={false}>
          <ThemedText style={styles.searchCTAText} accessible={false}>
            {dropoffAddress ? dropoffAddress : "Delivery address"}
          </ThemedText>
        </View>
      </TouchableOpacity>
      {!!errors.to_address && (
        <HelperText type="error" visible={!!errors.to_address}>
          {errors.to_address?.message}
        </HelperText>
      )}
    </>
  );

  const routeSheet = (
    <BottomSheetModal
      ref={routeSheetRef}
      index={1}
      snapPoints={routeSheetSnapPoints}
      style={desktopSheetStyle}
      keyboardBehavior="extend"
      keyboardBlurBehavior="restore"
      android_keyboardInputMode="adjustResize"
      backgroundStyle={[
        styles.routeSheetBackground,
        { backgroundColor: theme.background },
      ]}
      handleIndicatorStyle={styles.routeSheetHandle}
    >
      <BottomSheetView style={styles.routeSheetContent}>
        <View style={styles.routeSheetHeader}>
          <View accessible accessibilityRole="header">
            <ThemedText type="subtitle" testID="route-sheet-title">
              Enter your route
            </ThemedText>
            <ThemedText style={styles.routeSheetSubtitle} accessible={false}>
              Choose pickup and drop-off to continue
            </ThemedText>
          </View>
          <TouchableOpacity
            onPress={closeRouteSheet}
            style={[
              styles.routeCloseButton,
              {
                backgroundColor: theme.background,
                borderColor: theme.border,
              },
            ]}
          >
            <Ionicons name="close" size={20} color={theme.text} />
          </TouchableOpacity>
        </View>

        <View style={styles.inputGroup}>
          <ThemedText type="smallSemiBold">Pickup</ThemedText>
          <GooglePlacesInput
            ref={pickupRef}
            isPickoff
            testID="search-pickup-address"
            placeholder="Search pickup address..."
            isAbsolute={locationOnly ? false : true}
            onPlaceSelected={(place) => {
              setValue("from_address", place.address);
              setValue("from_latitude", place.latitude);
              setValue("from_longitude", place.longitude);
              closeRouteSheet();
            }}
          />
        </View>

        {!locationOnly && (
          <View style={styles.inputGroup}>
            <ThemedText type="smallSemiBold">Drop-off</ThemedText>
            <GooglePlacesInput
              ref={dropoffRef}
              testID="search-dropoff-address"
              placeholder="Search drop-off address..."
              onPlaceSelected={(place) => {
                setValue("to_address", place.address);
                setValue("to_latitude", place.latitude);
                setValue("to_longitude", place.longitude);
                closeRouteSheet();
              }}
            />
          </View>
        )}
      </BottomSheetView>
    </BottomSheetModal>
  );

  if (desktopLayout) {
    return (
      <>
        <CreateRequestLayout
          title={addressTitle}
          subtitle={
            locationOnly ? undefined : "Choose pickup and drop-off to continue"
          }
          step={2}
          totalSteps={5}
          onBack={handleBack}
          onNext={handleSubmit(onSubmit)}
          nextLabel={submitLabel}
          isNextLoading={isSubmitting}
        >
          <View style={[styles.desktopMap, { borderColor: theme.border }]}>
            {mapContent}
            {deliveryAddressField ? (
              <View
                style={[
                  styles.desktopCard,
                  styles.inputGroup,
                  { backgroundColor: theme.background },
                ]}
              >
                {deliveryAddressField}
              </View>
            ) : null}
          </View>
          {!!errors.from_address && (
            <HelperText type="error" visible>
              {errors.from_address?.message}
            </HelperText>
          )}
        </CreateRequestLayout>
        {routeSheet}
      </>
    );
  }

  const addressForm = (
    <View style={styles.inputGroup}>
      <Label testID="address-step-title" label={addressTitle} />
      {deliveryAddressField}
      <TouchableOpacity
        testID="address-step-next"
        accessible
        accessibilityRole="button"
        accessibilityLabel={submitLabel}
        style={[styles.button, isSubmitting && styles.buttonDisabled]}
        onPress={handleSubmit(onSubmit)}
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <>
            <ThemedText
              type="smallSemiBold"
              style={{ color: "#fff" }}
              accessible={false}
            >
              {submitLabel}
            </ThemedText>
            {submitLabel === "Next" && (
              <Ionicons
                name="chevron-forward"
                size={20}
                color="#fff"
                accessible={false}
              />
            )}
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top", "bottom"]}
    >
      <View style={[styles.mapWrapper, desktop && styles.desktopMapWrapper]}>
        <TouchableOpacity
          onPress={handleBack}
          style={[styles.backButton, { backgroundColor: theme.background }]}
        >
          <Ionicons name="chevron-back" size={24} color={theme.text} />
        </TouchableOpacity>
        {mapContent}

        {desktop ? (
          <View
            style={[styles.desktopCard, { backgroundColor: theme.background }]}
          >
            {addressForm}
          </View>
        ) : null}
      </View>

      {desktop ? null : (
        <BottomSheet
          ref={bottomSheetRef}
          snapPoints={snapPoints}
          handleIndicatorStyle={styles.sheetHandle}
          backgroundStyle={[
            styles.sheetBackground,
            { backgroundColor: theme.background },
          ]}
        >
          <BottomSheetScrollView
            contentContainerStyle={styles.sheetContent}
            showsVerticalScrollIndicator={false}
          >
            {addressForm}
          </BottomSheetScrollView>
        </BottomSheet>
      )}

      {routeSheet}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  mapWrapper: {
    width: "100%",
    height: "78%",
    backgroundColor: "#e5e7eb",
  },
  section: {
    marginBottom: 24,
  },
  map: {
    width: "100%",
    height: "100%",
  },
  inputRow: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#fff",
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 50,
  },
  storageInput: {
    flex: 1,
    fontSize: 16,
    color: BrandColors.charcoal,
    marginLeft: 12,
    marginRight: 8,
  },
  storageInputSuffix: {
    fontSize: 16,
    color: "#6b7280",
    fontWeight: "500",
  },
  imageUploadButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f3f4f6",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    borderStyle: "dashed",
    height: 120,
  },
  uploadedImage: {
    width: "100%",
    height: "100%",
    borderRadius: 8,
  },
  imageUploadText: {
    marginLeft: 8,
    fontSize: 14,
    color: BrandColors.charcoal,
  },
  pickupNowButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: "#f0f7ff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BrandColors.primary,
  },
  pickupNowButtonText: {
    fontSize: 16,
    color: BrandColors.primary,
    fontWeight: "800",
  },
  inputError: {
    borderColor: BrandColors.error,
    borderWidth: 1,
  },
  pickupBadge: {
    position: "absolute",
    top: "35%",
    alignSelf: "center",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 8,
    width: "70%",
    maxWidth: 600,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  locationIconContainer: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: BrandColors.primary,
    justifyContent: "center",
    alignItems: "center",
  },
  badgeTextContainer: {
    flex: 1,
  },
  badgeLabel: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginBottom: 4,
  },
  badgeValue: {
    fontSize: 16,
    fontWeight: "600",
  },
  locateButton: {
    position: "absolute",
    bottom: 40,
    right: 20,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: "#fff",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
  },
  sheetBackground: {
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
  },
  bottomPanel: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingTop: 8,
    paddingBottom: 24,
  },
  sheetHandle: {
    alignSelf: "center",
    backgroundColor: "#475569",
    width: 60,
    height: 4,
    borderRadius: 2,
    marginBottom: 12,
  },
  sheetContent: {
    paddingHorizontal: 20,
    paddingBottom: 80,
    gap: 18,
  },
  routeSheetBackground: {
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
  },
  routeSheetHandle: {
    backgroundColor: "#475569",
    width: 60,
  },
  routeSheetContent: {
    flex: 1,
    padding: 24,
    gap: 18,
  },
  routeSheetHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  routeSheetSubtitle: {
    color: BrandColors.mediumGray,
    marginTop: 4,
    fontSize: 13,
  },
  routeCloseButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
  },
  sheetIntro: {
    marginTop: 6,
  },
  sheetTitle: {
    fontSize: 20,
    fontWeight: "700",
    color: "#f8fafc",
  },
  sheetSubtitle: {
    fontSize: 14,
    color: "#9ca3af",
    marginTop: 4,
  },
  searchCard: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 12,
    borderWidth: 1,
  },
  searchCTAText: {
    fontWeight: "700",
  },
  helperText: {
    fontSize: 12,
    color: BrandColors.mediumGray,
    marginTop: 8,
    marginBottom: 12,
  },
  storageDaysContainer: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#9ca3af",
    marginBottom: 8,
  },
  quickSelectContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 8,
  },
  quickSelectChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    backgroundColor: "#f3f4f6",
    borderWidth: 1,
    borderColor: "transparent",
  },
  quickSelectChipSelected: {
    backgroundColor: "rgba(52, 211, 153, 0.1)",
    borderColor: BrandColors.primary,
  },
  quickSelectText: {
    fontSize: 14,
    color: "#4b5563",
    fontWeight: "500",
  },
  quickSelectTextSelected: {
    color: "#fff",
    fontWeight: "600",
  },
  inputGroup: {
    gap: 8,
  },
  textInput: {
    backgroundColor: "#f3f4f6",
    borderRadius: 12,
    padding: 12,
    fontSize: 16,
    color: BrandColors.charcoal,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  button: {
    backgroundColor: BrandColors.primary,
    borderRadius: 8,
    padding: 12,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.7,
  },
  backButton: {
    position: "absolute",
    top: 20,
    left: 20,
    zIndex: 100,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: 24,
    width: 48,
    height: 48,
    justifyContent: "center",
    alignItems: "center",
  },
  desktopMapWrapper: {
    height: "100%",
    position: "relative",
  },
  desktopCard: {
    position: "absolute",
    left: 24,
    bottom: 24,
    width: 420,
    maxWidth: "92%",
    borderRadius: 20,
    padding: 20,
    zIndex: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.12,
    shadowRadius: 16,
    elevation: 6,
  },
  desktopMap: {
    height: 500,
    position: "relative",
    backgroundColor: "#e5e7eb",
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  desktopLocateButton: {
    bottom: 16,
    right: 16,
  },
});
